// Inline project → file picker for the chat composer's Projects pill.
// Opens as a centered modal (no nav-away). Lists projects + files
// derived from the user's recent documents, lets them pick a file
// which then gets fetched, converted to a browser File, and handed
// back to the parent for attachment to the chat.
//
// Why recent documents (not a dedicated "files in folder" endpoint):
// listRecentDocuments already returns { fileId, fileName, folder?,
// lastUsedAt, conversationTitle } per file — enough to group
// client-side without a new backend route. If the user has files in a
// folder that haven't been touched recently, they won't show; that's
// V1 scope. Future iteration could add a dedicated /folders/:id/files
// endpoint when users complain.
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { APP } from './tokens';
import { listRecentDocuments, type RecentDocument } from '../../api/workflowApi';
import { AppFolderIcon } from './AppFolderIcon';

export interface FoldersFilePickerProps {
  open: boolean;
  /** Bearer-token getter for the file fetch. */
  getToken: () => string | null;
  /** Called once a file has been fetched + converted to a browser File.
   *  The parent attaches it to the composer's currentFiles state. */
  onPickFile: (file: File) => void;
  onClose: () => void;
}

interface PickerFolderGroup {
  /** Display name. 'No folder' for ungrouped files. */
  name: string;
  isUnfiled: boolean;
  isContracts: boolean;
  files: RecentDocument[];
}

const UNFILED_KEY = '__unfiled__';

export function FoldersFilePicker({
  open,
  getToken,
  onPickFile,
  onClose,
}: FoldersFilePickerProps) {
  const [docs, setDocs] = useState<RecentDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fetchingFileId, setFetchingFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSearch('');
    setError(null);
    listRecentDocuments(50)
      .then((list) => {
        if (cancelled) return;
        setDocs(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[FoldersFilePicker] fetch failed', err);
        setError('Couldn\'t load files. Try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const groups = useMemo<PickerFolderGroup[]>(() => {
    // Group recent docs by folder name. Files without a folder go into
    // a single "No folder" bucket at the bottom. Contracts folders
    // surface first (they're the gold path for the workflow flow).
    const byFolder = new Map<string, PickerFolderGroup>();
    for (const doc of docs) {
      const key = doc.folder?.name ?? UNFILED_KEY;
      const existing = byFolder.get(key);
      if (existing) {
        existing.files.push(doc);
      } else {
        byFolder.set(key, {
          name: doc.folder?.name ?? 'No folder',
          isUnfiled: !doc.folder,
          isContracts: !!doc.folder?.isContracts,
          files: [doc],
        });
      }
    }
    return [...byFolder.values()].sort((a, b) => {
      // Contracts folders first, then other folders alpha-sorted, then
      // the unfiled bucket at the end.
      if (a.isUnfiled !== b.isUnfiled) return a.isUnfiled ? 1 : -1;
      if (a.isContracts !== b.isContracts) return a.isContracts ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [docs]);

  const filteredGroups = useMemo(() => {
    // Only show groups that belong to a real folder — hide the "No folder" bucket.
    const foldered = groups.filter((g) => !g.isUnfiled);
    const q = search.trim().toLowerCase();
    if (!q) return foldered;
    return foldered
      .map((g) => ({
        ...g,
        files: g.files.filter((f) =>
          f.fileName.toLowerCase().includes(q)
          || f.conversationTitle?.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.name.toLowerCase().includes(q) || g.files.length > 0);
  }, [groups, search]);

  if (!open) return null;

  const handlePickFile = async (doc: RecentDocument) => {
    if (fetchingFileId) return;
    setFetchingFileId(doc.fileId);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`/api/file/${doc.fileId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error(`Couldn't fetch file (${res.status})`);
      }
      const blob = await res.blob();
      const file = new File(
        [blob],
        doc.fileName,
        { type: blob.type || 'application/octet-stream' },
      );
      onPickFile(file);
      onClose();
    } catch (err) {
      console.error('[FoldersFilePicker] fetch failed', err);
      setError(err instanceof Error ? err.message : 'Failed to attach file');
    } finally {
      setFetchingFileId(null);
    }
  };

  return (
    <div onClick={onClose} style={BACKDROP}>
      <div onClick={(e) => e.stopPropagation()} style={CARD}>
        <header style={HEADER}>
          <AppFolderIcon width={20} height={16} />
          <span style={TITLE}>Pull a document from a project</span>
        </header>

        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search files…"
          style={SEARCH_INPUT}
        />

        {error && <div style={ERROR_BANNER}>{error}</div>}

        <div style={BODY}>
          {loading && docs.length === 0 && <div style={EMPTY}>Loading…</div>}
          {!loading && groups.length === 0 && (
            <div style={EMPTY_BOX}>
              <div style={EMPTY_TITLE}>No documents yet</div>
              <div style={EMPTY_HINT}>
                Upload a file to a conversation and it'll show up here next time.
              </div>
            </div>
          )}

          {filteredGroups.map((group) => (
            <section key={group.name} style={FOLDER_SECTION}>
              <header style={FOLDER_HEADER}>
                <AppFolderIcon width={18} height={14} />
                <span style={FOLDER_NAME}>{group.name}</span>
                {group.isContracts && <span style={CONTRACTS_TAG}>contracts</span>}
              </header>
              {group.files.map((doc) => {
                const isFetching = fetchingFileId === doc.fileId;
                return (
                  <button
                    key={doc.fileId}
                    type="button"
                    onClick={() => handlePickFile(doc)}
                    disabled={!!fetchingFileId}
                    style={{
                      ...FILE_ROW,
                      cursor: fetchingFileId ? 'default' : 'pointer',
                      opacity: fetchingFileId && !isFetching ? 0.5 : 1,
                    }}
                  >
                    <span style={FILE_ICON}>📄</span>
                    <div style={FILE_TEXT_COL}>
                      <div style={FILE_NAME}>{doc.fileName}</div>
                      {doc.conversationTitle && (
                        <div style={FILE_SUB}>{doc.conversationTitle}</div>
                      )}
                    </div>
                    <div style={FILE_META}>
                      {isFetching ? 'Loading…' : new Date(doc.lastUsedAt).toLocaleDateString()}
                    </div>
                  </button>
                );
              })}
            </section>
          ))}
        </div>

        <footer style={FOOTER}>
          <button type="button" style={SECONDARY_BTN} onClick={onClose} disabled={!!fetchingFileId}>
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const BACKDROP: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 200,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: 'rgba(26, 23, 20, 0.45)',
};

const CARD: CSSProperties = {
  width: 560,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--app-bg-card-body)',
  borderRadius: 14,
  border: `1px solid ${APP.border}`,
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.25)',
  fontFamily: APP.fontFamily,
  overflow: 'hidden',
};

const HEADER: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '14px 18px 10px',
  borderBottom: `1px solid ${APP.border}`,
};

const TITLE: CSSProperties = { fontSize: 14, fontWeight: 600, color: APP.textPrimary };

const SEARCH_INPUT: CSSProperties = {
  margin: '12px 18px 0',
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  fontSize: 13,
  fontFamily: APP.fontFamily,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  outline: 'none',
};

const ERROR_BANNER: CSSProperties = {
  margin: '8px 18px 0',
  padding: '8px 12px',
  borderRadius: 8,
  fontSize: 12,
  color: '#B33A3A',
  backgroundColor: '#FBEAEA',
  border: '1px solid #F2C9C9',
};

const BODY: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const EMPTY: CSSProperties = { fontSize: 12, color: APP.textMuted, padding: '8px 0' };
const EMPTY_BOX: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  padding: '20px 12px',
  borderRadius: 10,
  backgroundColor: 'var(--app-bg-search)',
  border: `1px dashed ${APP.border}`,
};
const EMPTY_TITLE: CSSProperties = { fontSize: 14, fontWeight: 500, color: APP.textPrimary };
const EMPTY_HINT: CSSProperties = { fontSize: 12, color: APP.textMuted };

const FOLDER_SECTION: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 2,
};

const FOLDER_HEADER: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '4px 4px 4px 0',
};

const FOLDER_NAME: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const CONTRACTS_TAG: CSSProperties = {
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 3,
  backgroundColor: `${APP.accentWorkflowPrimary}1A`,
  color: APP.accentWorkflowPrimary,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const FILE_ROW: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 10px',
  border: '1px solid transparent',
  borderRadius: 8,
  background: 'transparent',
  textAlign: 'left',
  fontFamily: APP.fontFamily,
  width: '100%',
  minWidth: 0,
};

const FILE_ICON: CSSProperties = { fontSize: 16, flexShrink: 0 };

const FILE_TEXT_COL: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 2,
  flex: 1, minWidth: 0,
};

const FILE_NAME: CSSProperties = {
  fontSize: 13, fontWeight: 500, color: APP.textPrimary,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

const FILE_SUB: CSSProperties = {
  fontSize: 11, color: APP.textMuted,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

const FILE_META: CSSProperties = {
  fontSize: 11, color: APP.textMuted, flexShrink: 0,
};

const FOOTER: CSSProperties = {
  borderTop: `1px solid ${APP.border}`,
  padding: '12px 18px 14px',
  display: 'flex',
  justifyContent: 'flex-end',
};

const SECONDARY_BTN: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  background: 'transparent',
  color: APP.textPrimary,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};
